import React, { useEffect, useState } from 'react';
import { Plane, Plus, Edit, Trash2, Save, X, Loader2, RefreshCw, Calendar, DollarSign, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { api } from '../../utils/apiClient';

const FlightsManagementV2 = () => {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFlight, setEditingFlight] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState({
    flightNumber: '',
    newPrice: '',
    newDepartureTime: '',
    newArrivalTime: ''
  });
  const [formData, setFormData] = useState({
    flightNumber: '',
    origin: '',
    destination: '',
    departureTime: '',
    arrivalTime: '',
    price: 3666,
    availableSeats: 13,
    aircraft: 'Cessna 208 Grand Caravan',
    daysOfWeek: 'Lunes a Sábado'
  });

  useEffect(() => {
    loadFlights();
  }, []);

  const loadFlights = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/flights/all');
      setFlights(response.flights || []);
    } catch (error) {
      console.error('Error loading flights:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (flight) => {
    setEditingFlight(flight.id);
    setFormData({
      flightNumber: flight.flightNumber,
      origin: flight.origin,
      destination: flight.destination,
      departureTime: flight.departureTime,
      arrivalTime: flight.arrivalTime,
      price: flight.price,
      availableSeats: flight.availableSeats,
      aircraft: flight.aircraft,
      daysOfWeek: flight.daysOfWeek
    });
    setShowAddForm(false);
  };

  const handleSave = async () => {
    try {
      if (editingFlight) {
        // Update existing flight
        await api.put(`/api/admin/flights/${editingFlight}`, formData);
        alert('✅ Vuelo actualizado correctamente');
      } else {
        // Create new flight - this will create scheduled flights for next 90 days
        await api.post('/api/admin/flights/create-with-schedule', formData);
        alert('✅ Ruta creada correctamente. Se generaron vuelos para los próximos 90 días');
      }

      setEditingFlight(null);
      setShowAddForm(false);
      resetForm();
      await loadFlights();
    } catch (error) {
      console.error('Error saving flight:', error);
      alert('❌ Error al guardar el vuelo: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (flightId) => {
    if (!window.confirm('¿Estás seguro de cancelar este vuelo?')) return;

    try {
      await api.delete(`/api/admin/flights/${flightId}`);
      alert('✅ Vuelo cancelado correctamente');
      await loadFlights();
    } catch (error) {
      console.error('Error deleting flight:', error);
      alert('❌ Error al cancelar el vuelo');
    }
  };

  const handleBulkUpdatePrice = async () => {
    if (!bulkUpdateData.flightNumber || !bulkUpdateData.newPrice) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }

    try {
      const response = await api.post('/api/admin/flights/bulk-update', {
        flightNumber: bulkUpdateData.flightNumber,
        updates: {
          price: parseFloat(bulkUpdateData.newPrice),
          ...(bulkUpdateData.newDepartureTime && { departureTime: bulkUpdateData.newDepartureTime }),
          ...(bulkUpdateData.newArrivalTime && { arrivalTime: bulkUpdateData.newArrivalTime })
        }
      });
      
      alert(`✅ ${response.updated_count} vuelos futuros actualizados correctamente`);
      setShowBulkUpdateModal(false);
      setBulkUpdateData({ flightNumber: '', newPrice: '', newDepartureTime: '', newArrivalTime: '' });
      await loadFlights();
    } catch (error) {
      console.error('Error updating flights:', error);
      alert('❌ Error al actualizar vuelos: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      flightNumber: '',
      origin: '',
      destination: '',
      departureTime: '',
      arrivalTime: '',
      price: 3666,
      availableSeats: 13,
      aircraft: 'Cessna 208 Grand Caravan',
      daysOfWeek: 'Lunes a Sábado'
    });
  };

  const handleCancel = () => {
    setEditingFlight(null);
    setShowAddForm(false);
    resetForm();
  };

  // Get unique flight numbers for bulk update
  const uniqueFlightNumbers = [...new Set(flights.map(f => f.flightNumber))];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Gestión de Vuelos</h2>
          <p className="text-sm text-gray-600">Total de vuelos programados: {flights.length}</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowBulkUpdateModal(true)}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar Masivamente
          </Button>
          <Button
            onClick={() => {
              setShowAddForm(!showAddForm);
              setEditingFlight(null);
              resetForm();
            }}
            className="bg-blue-900 hover:bg-blue-800"
          >
            <Plus className="w-4 h-4 mr-2" />
            Agregar Nueva Ruta
          </Button>
        </div>
      </div>

      {/* Bulk Update Modal */}
      {showBulkUpdateModal && (
        <Card className="border-4 border-orange-400 bg-orange-50">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center">
              <RefreshCw className="w-5 h-5 mr-2 text-orange-600" />
              Actualización Masiva de Vuelos Futuros
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Los cambios se aplicarán a todos los vuelos futuros con el número seleccionado
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Número de Vuelo *</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={bulkUpdateData.flightNumber}
                  onChange={(e) => setBulkUpdateData({...bulkUpdateData, flightNumber: e.target.value})}
                >
                  <option value="">Seleccionar...</option>
                  {uniqueFlightNumbers.map(fn => (
                    <option key={fn} value={fn}>{fn}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Nuevo Precio (MXN) *</Label>
                <Input
                  type="number"
                  value={bulkUpdateData.newPrice}
                  onChange={(e) => setBulkUpdateData({...bulkUpdateData, newPrice: e.target.value})}
                  placeholder="3666"
                />
              </div>
              <div>
                <Label>Nueva Hora de Salida (opcional)</Label>
                <Input
                  type="time"
                  value={bulkUpdateData.newDepartureTime}
                  onChange={(e) => setBulkUpdateData({...bulkUpdateData, newDepartureTime: e.target.value})}
                />
              </div>
              <div>
                <Label>Nueva Hora de Llegada (opcional)</Label>
                <Input
                  type="time"
                  value={bulkUpdateData.newArrivalTime}
                  onChange={(e) => setBulkUpdateData({...bulkUpdateData, newArrivalTime: e.target.value})}
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-4">
              <Button onClick={handleBulkUpdatePrice} className="bg-orange-600 hover:bg-orange-700">
                <Save className="w-4 h-4 mr-2" />
                Aplicar Cambios a Vuelos Futuros
              </Button>
              <Button onClick={() => setShowBulkUpdateModal(false)} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingFlight) && (
        <Card className="border-2 border-blue-200">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold mb-4">
              {editingFlight ? 'Editar Vuelo Individual' : 'Nueva Ruta (se crearán vuelos para 90 días)'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Número de Vuelo *</Label>
                <Input
                  value={formData.flightNumber}
                  onChange={(e) => setFormData({...formData, flightNumber: e.target.value})}
                  placeholder="AT-105"
                />
              </div>
              <div>
                <Label>Aeronave</Label>
                <Input
                  value={formData.aircraft}
                  onChange={(e) => setFormData({...formData, aircraft: e.target.value})}
                />
              </div>
              <div>
                <Label>Origen (Ciudad, Estado) *</Label>
                <Input
                  value={formData.origin}
                  onChange={(e) => setFormData({...formData, origin: e.target.value})}
                  placeholder="Ej: Oaxaca de Juárez, Ciudad de México, Guadalajara"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Escribe cualquier ciudad de México</p>
              </div>
              <div>
                <Label>Destino (Ciudad, Estado) *</Label>
                <Input
                  value={formData.destination}
                  onChange={(e) => setFormData({...formData, destination: e.target.value})}
                  placeholder="Ej: Puerto Escondido, Cancún, Monterrey"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Escribe cualquier ciudad de México</p>
              </div>
              <div>
                <Label>Hora de Salida *</Label>
                <Input
                  type="time"
                  value={formData.departureTime}
                  onChange={(e) => setFormData({...formData, departureTime: e.target.value})}
                />
              </div>
              <div>
                <Label>Hora de Llegada *</Label>
                <Input
                  type="time"
                  value={formData.arrivalTime}
                  onChange={(e) => setFormData({...formData, arrivalTime: e.target.value})}
                />
              </div>
              <div>
                <Label>Precio (MXN) *</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                />
              </div>
              <div>
                <Label>Asientos Disponibles</Label>
                <Input
                  type="number"
                  value={formData.availableSeats}
                  onChange={(e) => setFormData({...formData, availableSeats: parseInt(e.target.value)})}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Días de Operación *</Label>
                <select
                  className="w-full border rounded-md p-2"
                  value={formData.daysOfWeek}
                  onChange={(e) => setFormData({...formData, daysOfWeek: e.target.value})}
                >
                  <option value="Lunes a Sábado">Lunes a Sábado</option>
                  <option value="Domingo">Solo Domingo</option>
                  <option value="Lunes a Viernes">Lunes a Viernes</option>
                  <option value="Sábados y Domingos">Sábados y Domingos</option>
                  <option value="Todos los días">Todos los días (Lunes a Domingo)</option>
                  <option value="Lunes, Miércoles, Viernes">Lunes, Miércoles, Viernes</option>
                  <option value="Martes, Jueves, Sábado">Martes, Jueves, Sábado</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Se generarán vuelos automáticamente para los próximos 90 días según los días seleccionados
                </p>
              </div>
            </div>
            <div className="flex space-x-3 mt-4">
              <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                {editingFlight ? 'Guardar Cambios' : 'Crear Ruta y Generar Vuelos'}
              </Button>
              <Button onClick={handleCancel} variant="outline">
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Flights List */}
      <div className="grid grid-cols-1 gap-4">
        {flights.map((flight) => (
          <Card key={flight.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Vuelo</p>
                    <p className="font-bold text-lg">{flight.flightNumber}</p>
                    <p className="text-xs text-gray-600">{flight.aircraft}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fecha</p>
                    <p className="font-bold text-blue-600">{flight.date}</p>
                    <p className="text-xs text-gray-600">{flight.daysOfWeek}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Ruta</p>
                    <p className="font-semibold">{flight.origin}</p>
                    <p className="text-sm">→ {flight.destination}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Horario</p>
                    <p className="font-semibold">{flight.departureTime} - {flight.arrivalTime}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Asientos / Precio</p>
                    <p className={`font-bold text-lg ${flight.availableSeats > 5 ? 'text-green-600' : flight.availableSeats > 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {flight.availableSeats} / 13 disponibles
                    </p>
                    <p className="font-semibold text-gray-900">${flight.price?.toLocaleString()} MXN</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    onClick={() => handleEdit(flight)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDelete(flight.id)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {flights.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Plane className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No hay vuelos programados</p>
            <p className="text-sm text-gray-500 mt-2">Agrega una nueva ruta para comenzar</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FlightsManagementV2;
